import { ArrowUpRight, BadgeCheck, Globe, Layers, Zap } from "lucide-react";
import feenImg from "../../assets/feen.png";
import yoolImg from "../../assets/yool1.png";
import tinImg from "../../assets/tinghir.png";

const PROJECTS = [
  {
    title: "Site vitrine premium",
    category: "Création de site",
    description:
      "Un site rapide et élégant, pensé pour la conversion et la confiance dès la première visite.",
    image: {
      src: feenImg,
      alt: "Maquette et design web",
    },
    highlights: ["Design UI/UX", "Performance", "Responsive"],
    link: "https://feen-art.com",
  },
  {
    title: "Landing page SEO",
    category: "Optimisation & SEO",
    description:
      "Structure, contenu et SEO technique optimisés pour gagner en visibilité et améliorer le taux de conversion.",
    image: {
      src: tinImg,
      alt: "Tableau de bord analytics",
    },
    highlights: ["SEO technique", "Core Web Vitals", "Copywriting"],
    link: "https://www.tinghir-guide.com",
  },
  {
    title: "Gestion utilisateurs & cartes d’accès (YOOL)",
    category: "Développement d'applications",
    description:
      "Système de gestion des utilisateurs et de cartes d’accès pour espaces de formation — fiable et aligné sur le cahier des charges.",
    image: {
      src: yoolImg,
      alt: "Application web sur mesure",
    },
    highlights: ["React", "Node.js", "Express", "MySQL"],
    link: "",
  },
];

function toHref(link) {
  return link.startsWith("http") ? link : `https://${link}`;
}

export function ProjectsSection() {
  return (
    <section id="projects" className="relative w-full overflow-hidden bg-surface py-24 md:py-32">
      <div className="pointer-events-none absolute inset-0 bg-hero-glow opacity-30" aria-hidden />
      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mx-auto mb-16 max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand">Portfolio</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white md:text-5xl">
            Réalisations
          </h2>
          <p className="mt-4 text-balance text-lg text-zinc-500">
Découvrez quelques-uns des projets sur lesquels j’ai travaillé.          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {PROJECTS.map((project) => {
            const CardWrapper = project.link ? "a" : "div";
            const linkProps = project.link
              ? {
                  href: toHref(project.link),
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {};

            return (
              <CardWrapper
                key={project.title}
                {...linkProps}
                className={[
                  "group overflow-hidden rounded-2xl border border-white/[0.06] bg-surface-card shadow-card",
                  project.link
                    ? "cursor-pointer transition duration-300 hover:border-brand/25 hover:shadow-glow"
                    : "",
                ].join(" ")}
              >
                <div className="relative aspect-[16/11] overflow-hidden">
                  <img
                    src={project.image.src}
                    alt={project.image.alt}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/20 to-transparent opacity-95" />
                  <div className="absolute left-4 top-4 inline-flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-white/10 bg-black/50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-200 backdrop-blur-md">
                    <Layers className="h-3.5 w-3.5 text-brand" />
                    <span className="truncate">{project.category}</span>
                  </div>
                </div>

                <div className="p-6 md:p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-lg font-semibold text-white md:text-xl">
                      {project.title}
                    </h3>
                    <span
                      className={[
                        "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.04] text-brand transition-colors",
                        project.link
                          ? "group-hover:border-brand/30 group-hover:bg-brand/10"
                          : "",
                      ].join(" ")}
                    >
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-500">{project.description}</p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {project.highlights.map((h) => (
                      <span
                        key={h}
                        className="rounded-full border border-white/[0.06] bg-white/[0.03] px-3 py-1 text-xs font-medium text-zinc-400"
                      >
                        {h}
                      </span>
                    ))}
                  </div>

                  {project.link ? (
                    <p className="mt-4 truncate text-xs text-zinc-600 transition-colors group-hover:text-brand/90">
                      {project.link.replace(/^https?:\/\//, "")}
                    </p>
                  ) : null}
                </div>
              </CardWrapper>
            );
          })}
        </div>

        <div className="mx-auto mt-14 max-w-5xl rounded-2xl border border-white/[0.08] bg-surface-card p-8 md:p-10">
          <div className="grid gap-8 md:grid-cols-3 md:gap-10">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <Zap className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-display font-semibold text-white">Performance</p>
                <p className="mt-1 text-sm text-zinc-500">
                  Temps de chargement et expérience utilisateur au cœur de chaque livrable.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <BadgeCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-display font-semibold text-white">Qualité</p>
                <p className="mt-1 text-sm text-zinc-500">
                  Code propre, design cohérent et processus de livraison transparent.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                <Globe className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-display font-semibold text-white">Visibilité</p>
                <p className="mt-1 text-sm text-zinc-500">
                  SEO et contenu pour attirer les bonnes opportunités.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
